import React, { useState } from 'react';
import ProductManagement from '../components/ProductManagement';
import SliderManagement from '../components/SliderManagement';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState(''); // Store password after login
  const [activeTab, setActiveTab] = useState('orders'); // 'orders', 'products', 'slider', 'users'
  const [orders, setOrders] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loginError, setLoginError] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOrders, setUserOrders] = useState([]);

  console.log('AdminDashboard component mounted');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setIsLoggedIn(true);
        setAdminPassword(password);
        setPassword('');
        await fetchOrders(password);
        await fetchStatistics(password);
      } else {
        setLoginError('Invalid password');
        setPassword('');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Error connecting to server');
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async (passwordToUse = null, status = '', page = 1) => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const query = new URLSearchParams();
      if (status) query.append('status', status);
      query.append('page', page);
      query.append('limit', 10);

      const response = await fetch(`${apiUrl}/admin/orders?${query}`, {
        headers: {
          'x-admin-password': passwordToUse || adminPassword
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setOrders(data.orders);
        setTotalPages(data.pagination?.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
    setLoading(false);
  };

  const fetchStatistics = async (passwordToUse = null) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/admin/statistics`, {
        headers: {
          'x-admin-password': passwordToUse || adminPassword
        }
      });
      const data = await response.json();
      if (data.success) {
        setStatistics(data.statistics);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/admin/users`, {
        headers: {
          'x-admin-password': adminPassword
        }
      });
      const data = await response.json();
      if (data.success) {
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/admin/users/${userId}`, {
        headers: {
          'x-admin-password': adminPassword
        }
      });
      const data = await response.json();
      if (data.success) {
        setSelectedUser(data.user);
        setUserOrders(data.orders);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': adminPassword
        },
        body: JSON.stringify({ status })
      });
      const data = await response.json();
      
      if (data.success) {
        // Update the order in the local orders array immediately
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.orderId === orderId ? { ...order, status, updatedAt: new Date() } : order
          )
        );
        
        // Update the selected order with new data
        setSelectedOrder(data.order);
        
        // Refresh the orders list from server
        fetchOrders(null, filterStatus, currentPage);
        
        alert('Order status updated successfully');
      }
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Error updating order');
    }
  };

  const handleFilterChange = (status) => {
    setFilterStatus(status);
    setCurrentPage(1);
    fetchOrders(null, status, 1);
  };

  if (!isLoggedIn) {
    return (
      <div className="admin-login-container">
        <div className="login-card">
          <h1>Admin Dashboard</h1>
          <p>Enter password to access orders</p>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              autoFocus
              disabled={loading}
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          {loginError && <p className="error-message" style={{color: 'red', marginTop: '10px'}}>{loginError}</p>}
          <p className="hint"></p>
        </div>
      </div>
    );
  }

  const filteredOrders = orders.filter(order =>
    order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customerInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div>
          <h1>TRYON Admin Dashboard</h1>
          <p>Manage orders and track sales</p>
        </div>
        <button className="logout-btn" onClick={() => setIsLoggedIn(false)}>
          Logout
        </button>
      </header>

      {/* Tab Navigation */}
      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          📦 Orders
        </button>
        <button 
          className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          🛍️ Products
        </button>
        <button 
          className={`tab-btn ${activeTab === 'slider' ? 'active' : ''}`}
          onClick={() => setActiveTab('slider')}
        >
          🎨 Slider
        </button>
        <button 
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('users');
            fetchUsers();
          }}
        >
          👥 Users
        </button>
      </div>

      {/* Orders Tab Content */}
      {activeTab === 'orders' && (
        <>
          {statistics && (
            <div className="statistics-grid">
              <div className="stat-card">
                <div className="stat-value">{statistics.totalOrders}</div>
                <div className="stat-label">Total Orders</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">₹{statistics.totalRevenue}</div>
                <div className="stat-label">Total Revenue</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{statistics.ordersByStatus?.confirmed || 0}</div>
                <div className="stat-label">Confirmed</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{statistics.ordersByStatus?.shipped || 0}</div>
                <div className="stat-label">Shipped</div>
              </div>
            </div>
          )}

      <div className="orders-section">
        <div className="orders-header">
          <h2>Orders</h2>
          <button className="refresh-btn" onClick={() => fetchOrders(null, filterStatus, currentPage)}>
            🔄 Refresh
          </button>
        </div>

        <div className="filters">
          <input
            type="text"
            placeholder="Search order number or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <div className="status-filters">
            <button
              className={`filter-btn ${filterStatus === '' ? 'active' : ''}`}
              onClick={() => handleFilterChange('')}
            >
              All Orders
            </button>
            <button
              className={`filter-btn ${filterStatus === 'pending' ? 'active' : ''}`}
              onClick={() => handleFilterChange('pending')}
            >
              Pending
            </button>
            <button
              className={`filter-btn ${filterStatus === 'confirmed' ? 'active' : ''}`}
              onClick={() => handleFilterChange('confirmed')}
            >
              Confirmed
            </button>
            <button
              className={`filter-btn ${filterStatus === 'shipped' ? 'active' : ''}`}
              onClick={() => handleFilterChange('shipped')}
            >
              Shipped
            </button>
            <button
              className={`filter-btn ${filterStatus === 'delivered' ? 'active' : ''}`}
              onClick={() => handleFilterChange('delivered')}
            >
              Delivered
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading orders...</p>
          </div>
        ) : filteredOrders.length > 0 ? (
          <>
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(order => (
                  <tr key={order._id}>
                    <td className="order-number">{order.orderNumber}</td>
                    <td>{order.customerInfo?.firstName} {order.customerInfo?.lastName}</td>
                    <td>{order.customerInfo?.email}</td>
                    <td>₹{order.total}</td>
                    <td>
                      <span className={`status-badge status-${order.status}`}>
                        {order.status}
                      </span>
                    </td>
                    <td>{order.paymentInfo?.method === 'razorpay' ? '💳 Online' : '💰 COD'}</td>
                    <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="view-btn"
                        onClick={() => setSelectedOrder(order)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pagination">
              <button
                onClick={() => {
                  setCurrentPage(Math.max(1, currentPage - 1));
                  fetchOrders(filterStatus, Math.max(1, currentPage - 1));
                }}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => {
                  setCurrentPage(Math.min(totalPages, currentPage + 1));
                  fetchOrders(filterStatus, Math.min(totalPages, currentPage + 1));
                }}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          </>
        ) : (
          <div className="no-orders">No orders found</div>
        )}
      </div>
      </>
      )}

      {/* Products Tab Content */}
      {activeTab === 'products' && (
        <ProductManagement adminPassword={adminPassword} />
      )}

      {/* Slider Tab Content */}
      {activeTab === 'slider' && (
        <SliderManagement adminPassword={adminPassword} />
      )}

      {/* Users Tab Content */}
      {activeTab === 'users' && (
        <div className="users-section">
          <div className="section-header">
            <h2>👥 Users Management</h2>
            <p className="total-count">Total Users: {users.length}</p>
          </div>

          {loading ? (
            <div className="loading-state">Loading users...</div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Orders</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user._id}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.phone || 'N/A'}</td>
                      <td>
                        <span className={`status-badge ${user.isVerified ? 'verified' : 'unverified'}`}>
                          {user.isVerified ? '✓ Verified' : '✗ Unverified'}
                        </span>
                      </td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td>{user.orderCount || 0}</td>
                      <td>
                        <button
                          className="view-btn"
                          onClick={() => fetchUserDetails(user._id)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <div className="empty-state">No users found</div>
              )}
            </div>
          )}
        </div>
      )}

      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="close-btn"
              onClick={() => setSelectedOrder(null)}
            >
              ✕
            </button>

            <h2>Order Details</h2>
            
            <div className="order-details">
              <div className="detail-section">
                <h3>Order Information</h3>
                <p><strong>Order #:</strong> {selectedOrder.orderNumber}</p>
                <p><strong>Status:</strong> {selectedOrder.status}</p>
                <p><strong>Date:</strong> {new Date(selectedOrder.createdAt).toLocaleString()}</p>
                <p><strong>Total:</strong> ₹{selectedOrder.total}</p>
              </div>

              <div className="detail-section">
                <h3>Customer Information</h3>
                <p><strong>Name:</strong> {selectedOrder.customerInfo?.firstName} {selectedOrder.customerInfo?.lastName}</p>
                <p><strong>Email:</strong> {selectedOrder.customerInfo?.email}</p>
                <p><strong>Phone:</strong> {selectedOrder.customerInfo?.phone}</p>
                <p><strong>Address:</strong> {selectedOrder.customerInfo?.address}, {selectedOrder.customerInfo?.city}, {selectedOrder.customerInfo?.state} {selectedOrder.customerInfo?.zipCode}</p>
              </div>

              <div className="detail-section">
                <h3>Items</h3>
                <ul className="items-list">
                  {selectedOrder.items?.map((item, idx) => (
                    <li key={idx}>
                      <span>{item.name}</span>
                      <span>{item.size}/{item.color}</span>
                      <span>Qty: {item.quantity}</span>
                      <span>₹{item.price}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="detail-section">
                <h3>Payment Information</h3>
                <p><strong>Method:</strong> {selectedOrder.paymentInfo?.method === 'razorpay' ? 'Online Payment (Razorpay)' : 'Cash on Delivery'}</p>
                <p><strong>Status:</strong> {selectedOrder.paymentInfo?.status || 'Pending'}</p>
                {selectedOrder.paymentInfo?.payment_id && (
                  <p><strong>Payment ID:</strong> {selectedOrder.paymentInfo.payment_id}</p>
                )}
              </div>

              <div className="detail-section">
                <h3>Update Status</h3>
                <div className="status-update">
                  <select
                    value={selectedOrder.status}
                    onChange={(e) => updateOrderStatus(selectedOrder.orderId, e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal-content user-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="close-btn"
              onClick={() => setSelectedUser(null)}
            >
              ✕
            </button>

            <h2>User Details</h2>

            <div className="user-details">
              <div className="detail-section">
                <h3>Personal Information</h3>
                <p><strong>Name:</strong> {selectedUser.name}</p>
                <p><strong>Email:</strong> {selectedUser.email}</p>
                <p><strong>Phone:</strong> {selectedUser.phone || 'N/A'}</p>
                <p>
                  <strong>Status:</strong>{' '}
                  <span className={`status-badge ${selectedUser.isVerified ? 'verified' : 'unverified'}`}>
                    {selectedUser.isVerified ? '✓ Verified' : '✗ Unverified'}
                  </span>
                </p>
                <p><strong>Joined:</strong> {new Date(selectedUser.createdAt).toLocaleString()}</p>
              </div>

              {selectedUser.address && (
                <div className="detail-section">
                  <h3>Address</h3>
                  <p>{selectedUser.address.street}</p>
                  <p>{selectedUser.address.city}, {selectedUser.address.state} {selectedUser.address.zipCode}</p>
                  <p>{selectedUser.address.country}</p>
                </div>
              )}

              <div className="detail-section">
                <h3>Order History ({userOrders.length} orders)</h3>
                {userOrders.length > 0 ? (
                  <div className="orders-history">
                    <table className="orders-table">
                      <thead>
                        <tr>
                          <th>Order ID</th>
                          <th>Date</th>
                          <th>Items</th>
                          <th>Total</th>
                          <th>Status</th>
                          <th>Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userOrders.map((order) => (
                          <tr key={order._id}>
                            <td>{order.orderNumber}</td>
                            <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                            <td>
                              <div className="order-items-summary">
                                {order.items.map((item, idx) => (
                                  <div key={idx} className="item-line">
                                    {item.name} x {item.quantity}
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td>₹{order.total || 0}</td>
                            <td>
                              <span className={`status-badge status-${order.status}`}>
                                {order.status}
                              </span>
                            </td>
                            <td>
                              <div>
                                <div>{order.paymentInfo?.method?.toUpperCase() || 'N/A'}</div>
                                <div className="payment-status">
                                  {order.paymentInfo?.status || 'Pending'}
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="empty-state">No orders placed yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
